/**
 * NutritionGoalsEditor - Unified component for editing nutrition goals
 * Used in: Admin Portal, Customer Portal, Mobile App (via API)
 * 
 * Features:
 * - Daily calorie goal
 * - Macro distribution as percentages (must sum to 100%)
 * - Meal distribution (breakfast/lunch/dinner/snacks = 100%)
 * - Preset profiles (High Protein, Low Carb, Balanced, Keto)
 * - Automatic gram calculation from percentages
 * - BMR editing for admin (manual from diagnostic measurements)
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Pencil, Lightbulb, Flame, Dumbbell, PieChart, Check, AlertTriangle, UtensilsCrossed, Sunrise, Sun, Moon, Cookie, Droplet, Scale, Salad, Leaf, Activity } from 'lucide-react';

// Macro conversion constants
const PROTEIN_KCAL_PER_GRAM = 4;
const CARBS_KCAL_PER_GRAM = 4;
const FAT_KCAL_PER_GRAM = 9;

// Preset profiles
const MACRO_PRESETS = {
  balanced: {
    name: 'Ausgewogen',
    icon: Scale,
    description: 'Klassische ausgewogene Ernährung',
    protein_percent: 25,
    carbs_percent: 50,
    fat_percent: 25
  },
  high_protein: {
    name: 'High Protein',
    icon: Dumbbell,
    description: 'Für Muskelaufbau & Abnehmen',
    protein_percent: 30,
    carbs_percent: 40,
    fat_percent: 30
  },
  low_carb: {
    name: 'Low Carb',
    icon: Salad,
    description: 'Wenig Kohlenhydrate',
    protein_percent: 30,
    carbs_percent: 25,
    fat_percent: 45
  },
  keto: {
    name: 'Keto',
    icon: Leaf,
    description: 'Ketogene Ernährung',
    protein_percent: 25,
    carbs_percent: 5,
    fat_percent: 70
  },
  athletic: {
    name: 'Sportler',
    icon: Activity,
    description: 'Für intensive Aktivität',
    protein_percent: 30,
    carbs_percent: 50,
    fat_percent: 20
  }
};

// Meal distribution presets
const MEAL_PRESETS = {
  standard: {
    name: 'Standard',
    breakfast: 25,
    lunch: 35,
    dinner: 30,
    snacks: 10
  },
  big_breakfast: {
    name: 'Grosses Frühstück',
    breakfast: 35,
    lunch: 30,
    dinner: 25,
    snacks: 10
  },
  big_dinner: {
    name: 'Grosses Abendessen',
    breakfast: 20,
    lunch: 30,
    dinner: 40,
    snacks: 10
  },
  many_snacks: {
    name: 'Viele Snacks',
    breakfast: 20,
    lunch: 30,
    dinner: 30,
    snacks: 20
  }
};

// Calculate grams from percentage and calories
const calculateGrams = (percent, totalCalories, kcalPerGram) => {
  const kcal = (percent / 100) * totalCalories;
  return Math.round(kcal / kcalPerGram);
};

// Calculate percentage from grams and calories
const calculatePercent = (grams, totalCalories, kcalPerGram) => {
  if (!grams || !totalCalories) return 0;
  const kcal = grams * kcalPerGram;
  return Math.round((kcal / totalCalories) * 100);
};

// Calculate meal calories from percentage
const calculateMealCalories = (percent, totalCalories) => {
  return Math.round((percent / 100) * totalCalories);
};

const NutritionGoalsEditor = ({ 
  customerId, 
  token, 
  apiUrl,
  isAdmin = false,
  onSave,
  compact = false,
  initialGoals = null
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bmrData, setBmrData] = useState(null);
  const [bmrDialogOpen, setBmrDialogOpen] = useState(false);
  const [savingBmr, setSavingBmr] = useState(false);
  const [bmrForm, setBmrForm] = useState({
    bmr_source: 'calculated',
    manual_bmr: '',
    bmr_measurement_device: '',
    bmr_measurement_date: new Date().toISOString().split('T')[0]
  });
  const [goals, setGoals] = useState({
    daily_calories: 2000,
    protein_percent: 25,
    carbs_percent: 50,
    fat_percent: 25,
    protein_per_kg: null,
    meal_distribution: {
      breakfast: 25,
      lunch: 35,
      dinner: 30,
      snacks: 10
    },
    water_goal_ml: 2500,
    preset: 'balanced'
  });

  // Load BMR data (admin only)
  const loadBmrData = async () => {
    if (!isAdmin || !customerId) return;
    try {
      const response = await axios.get(
        `${apiUrl}/nutrition/customer/${customerId}/bmr`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBmrData(response.data);
      setBmrForm({
        bmr_source: response.data.bmr_source || 'calculated',
        manual_bmr: response.data.manual_bmr || '',
        bmr_measurement_device: response.data.bmr_measurement_device || '',
        bmr_measurement_date: response.data.bmr_measurement_date?.split('T')[0] || new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Error loading BMR:', error);
    }
  };

  // Save BMR data
  const saveBmrData = async () => {
    if (bmrForm.bmr_source === 'manual') {
      const bmrValue = parseInt(bmrForm.manual_bmr);
      if (!bmrValue || bmrValue < 500 || bmrValue > 5000) {
        toast.error('BMR muss zwischen 500 und 5000 kcal liegen');
        return;
      }
    }
    
    setSavingBmr(true);
    try {
      await axios.put(
        `${apiUrl}/nutrition/customer/${customerId}/bmr`,
        {
          bmr_source: bmrForm.bmr_source,
          manual_bmr: bmrForm.bmr_source === 'manual' ? parseInt(bmrForm.manual_bmr) : null,
          bmr_measurement_device: bmrForm.bmr_source === 'manual' ? bmrForm.bmr_measurement_device : null,
          bmr_measurement_date: bmrForm.bmr_measurement_date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('BMR wurde aktualisiert');
      setBmrDialogOpen(false);
      loadBmrData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingBmr(false);
    }
  };

  // Load existing goals
  useEffect(() => {
    if (initialGoals) {
      setGoals(prev => ({ ...prev, ...initialGoals }));
      setLoading(false);
    } else {
      loadGoals();
    }
    // Load BMR for admin
    if (isAdmin) {
      loadBmrData();
    }
  }, [customerId]);

  const loadGoals = async () => {
    try {
      const endpoint = isAdmin 
        ? `${apiUrl}/nutrition/customer/${customerId}/goals`
        : `${apiUrl}/portal/nutrition/goals`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setGoals(prev => ({
          ...prev,
          daily_calories: response.data.daily_calorie_goal || response.data.daily_calories || 2000,
          protein_percent: response.data.protein_percent || 25,
          carbs_percent: response.data.carbs_percent || 50,
          fat_percent: response.data.fat_percent || 25,
          protein_per_kg: response.data.protein_per_kg || null,
          meal_distribution: response.data.meal_distribution || prev.meal_distribution,
          water_goal_ml: response.data.water_goal_ml || 2500,
          preset: response.data.macro_preset || 'balanced'
        }));
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate macros sum to 100%
    const macroSum = goals.protein_percent + goals.carbs_percent + goals.fat_percent;
    if (macroSum !== 100) {
      toast.error(`Makros müssen 100% ergeben (aktuell: ${macroSum}%)`);
      return;
    }

    // Validate meal distribution sums to 100%
    const mealSum = goals.meal_distribution.breakfast + goals.meal_distribution.lunch + 
                    goals.meal_distribution.dinner + goals.meal_distribution.snacks;
    if (mealSum !== 100) {
      toast.error(`Mahlzeiten müssen 100% ergeben (aktuell: ${mealSum}%)`);
      return;
    }

    setSaving(true);
    try {
      const endpoint = isAdmin 
        ? `${apiUrl}/nutrition/customer/${customerId}/goals`
        : `${apiUrl}/portal/nutrition/goals`;
      
      // Calculate gram values for storage
      const proteinGrams = calculateGrams(goals.protein_percent, goals.daily_calories, PROTEIN_KCAL_PER_GRAM);
      const carbsGrams = calculateGrams(goals.carbs_percent, goals.daily_calories, CARBS_KCAL_PER_GRAM);
      const fatGrams = calculateGrams(goals.fat_percent, goals.daily_calories, FAT_KCAL_PER_GRAM);

      const payload = {
        daily_calorie_goal: goals.daily_calories,
        daily_calories: goals.daily_calories,
        daily_protein: proteinGrams,
        daily_carbs: carbsGrams,
        daily_fat: fatGrams,
        protein_percent: goals.protein_percent,
        carbs_percent: goals.carbs_percent,
        fat_percent: goals.fat_percent,
        protein_per_kg: goals.protein_per_kg,
        protein_goal: proteinGrams,
        carbs_goal: carbsGrams,
        fat_goal: fatGrams,
        meal_distribution: goals.meal_distribution,
        water_goal_ml: goals.water_goal_ml,
        macro_preset: goals.preset
      };

      await axios.put(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Ernährungsziele gespeichert');
      onSave?.(payload);
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const applyMacroPreset = (presetKey) => {
    const preset = MACRO_PRESETS[presetKey];
    if (preset) {
      setGoals(prev => ({
        ...prev,
        protein_percent: preset.protein_percent,
        carbs_percent: preset.carbs_percent,
        fat_percent: preset.fat_percent,
        preset: presetKey
      }));
    }
  };

  const applyMealPreset = (presetKey) => {
    const preset = MEAL_PRESETS[presetKey];
    if (preset) {
      setGoals(prev => ({
        ...prev,
        meal_distribution: {
          breakfast: preset.breakfast,
          lunch: preset.lunch,
          dinner: preset.dinner,
          snacks: preset.snacks
        }
      }));
    }
  };

  // Calculate derived values
  const proteinGrams = calculateGrams(goals.protein_percent, goals.daily_calories, PROTEIN_KCAL_PER_GRAM);
  const carbsGrams = calculateGrams(goals.carbs_percent, goals.daily_calories, CARBS_KCAL_PER_GRAM);
  const fatGrams = calculateGrams(goals.fat_percent, goals.daily_calories, FAT_KCAL_PER_GRAM);
  
  const macroSum = goals.protein_percent + goals.carbs_percent + goals.fat_percent;
  const mealSum = goals.meal_distribution.breakfast + goals.meal_distribution.lunch + 
                  goals.meal_distribution.dinner + goals.meal_distribution.snacks;

  if (loading) {
    return <div className="p-4 text-center text-white/70">Laden...</div>;
  }

  return (
    <div className={`space-y-4 ${compact ? 'text-sm' : ''}`}>
      {/* BMR Section - Admin Only */}
      {isAdmin && bmrData && (
        <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-purple-500" />
              <div>
                <h3 className="font-semibold">Grundumsatz (BMR)</h3>
                <p className="text-xs text-white/70">
                  {bmrData.bmr_source === 'manual' 
                    ? 'Manuell erfasst (Diagnostik-Messung)' 
                    : 'Automatisch berechnet'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBmrDialogOpen(true)}
              className="border-purple-300 hover:bg-purple-100"
              data-testid="edit-bmr-goals-button"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-white/70 p-2 rounded border border-purple-200">
              <p className="text-xs text-white/70">Quelle</p>
              <p className="text-sm font-bold">
                {bmrData.bmr_source === 'manual' ? 'Diagnostik' : 'Formel'}
              </p>
            </div>
            {bmrData.bmr_source === 'manual' && bmrData.manual_bmr && (
              <>
                <div className="bg-white/70 p-2 rounded border border-purple-200">
                  <p className="text-xs text-white/70">BMR</p>
                  <p className="text-sm font-bold">{bmrData.manual_bmr} kcal</p>
                </div>
                <div className="bg-white/70 p-2 rounded border border-purple-200">
                  <p className="text-xs text-white/70">Messgerät</p>
                  <p className="text-sm font-bold truncate">{bmrData.bmr_measurement_device || '-'}</p>
                </div>
                <div className="bg-white/70 p-2 rounded border border-purple-200">
                  <p className="text-xs text-white/70">Datum</p>
                  <p className="text-sm font-bold">
                    {bmrData.bmr_measurement_date 
                      ? new Date(bmrData.bmr_measurement_date).toLocaleDateString('de-CH')
                      : '-'}
                  </p>
                </div>
              </>
            )}
          </div>
          
          {bmrData.bmr_source !== 'manual' && (
            <p className="text-xs text-purple-600 mt-2">
              Tipp: Nach einer Uvida Spirometrie können Sie hier den gemessenen BMR eintragen.
            </p>
          )}
        </div>
      )}

      {/* BMR Edit Dialog */}
      {isAdmin && (
        <Dialog open={bmrDialogOpen} onOpenChange={setBmrDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-4 h-4" /> BMR bearbeiten
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>BMR-Quelle</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={bmrForm.bmr_source}
                  onChange={(e) => setBmrForm({ ...bmrForm, bmr_source: e.target.value })}
                  data-testid="bmr-source-goals-select"
                >
                  <option value="calculated">Automatisch berechnet (Formel)</option>
                  <option value="manual">Manuell (Diagnostik-Messung)</option>
                </select>
              </div>

              {bmrForm.bmr_source === 'manual' && (
                <>
                  <div className="space-y-2">
                    <Label>BMR-Wert (kcal/Tag) *</Label>
                    <Input
                      type="number"
                      placeholder="z.B. 1650"
                      value={bmrForm.manual_bmr}
                      onChange={(e) => setBmrForm({ ...bmrForm, manual_bmr: e.target.value })}
                      min={500}
                      max={5000}
                      data-testid="manual-bmr-goals-input"
                    />
                    <p className="text-xs text-white/70">Typischer Bereich: 1200-2500 kcal</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Messgerät</Label>
                    <select
                      className="w-full border rounded-md p-2"
                      value={bmrForm.bmr_measurement_device}
                      onChange={(e) => setBmrForm({ ...bmrForm, bmr_measurement_device: e.target.value })}
                      data-testid="measurement-device-goals-select"
                    >
                      <option value="">Gerät auswählen...</option>
                      <option value="Uvida Spirometrie">Uvida Spirometrie</option>
                      <option value="InBody 770">InBody 770</option>
                      <option value="InBody 570">InBody 570</option>
                      <option value="Tanita MC-780">Tanita MC-780</option>
                      <option value="COSMED">COSMED</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label>Messdatum</Label>
                    <Input
                      type="date"
                      value={bmrForm.bmr_measurement_date}
                      onChange={(e) => setBmrForm({ ...bmrForm, bmr_measurement_date: e.target.value })}
                      data-testid="measurement-date-goals-input"
                    />
                  </div>
                </>
              )}

              {bmrForm.bmr_source === 'calculated' && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">ℹ️ Automatische Berechnung</p>
                  <p>Der BMR wird aus den Körperdaten berechnet (Gewicht, Grösse, Alter, Geschlecht).</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBmrDialogOpen(false)}
                disabled={savingBmr}
              >
                Abbrechen
              </Button>
              <Button
                onClick={saveBmrData}
                disabled={savingBmr}
                className="bg-purple-500 hover:bg-purple-600"
                data-testid="save-bmr-goals-button"
              >
                {savingBmr ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Calorie Goal */}
      <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-6 h-6 text-orange-500" />
          <div>
            <h3 className="font-semibold">Tagesziel</h3>
            <p className="text-xs text-white/70">Kalorien pro Tag</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="1000"
            max="6000"
            step="50"
            value={goals.daily_calories}
            onChange={(e) => setGoals(prev => ({ ...prev, daily_calories: parseInt(e.target.value) || 2000 }))}
            className="text-2xl font-bold text-center flex-1"
          />
          <span className="text-white/80 font-medium">kcal</span>
        </div>
      </div>

      {/* Protein per kg Body Weight */}
      <div className="p-4 bg-white/5 rounded-lg border">
        <div className="flex items-center gap-2 mb-3">
          <Dumbbell className="w-5 h-5 text-blue-500" />
          <div>
            <h3 className="font-semibold">Protein pro kg Körpergewicht</h3>
            <p className="text-xs text-white/70">Berechnet Protein basierend auf deinem Gewicht</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="1.0"
            max="3.0"
            step="0.1"
            value={goals.protein_per_kg || 1.6}
            onChange={(e) => {
              const ppk = parseFloat(e.target.value);
              setGoals(prev => ({ ...prev, protein_per_kg: ppk }));
            }}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            data-testid="protein-per-kg-editor"
          />
          <div className="text-right min-w-[80px]">
            <span className="text-lg font-bold text-blue-600">{(goals.protein_per_kg || 1.6).toFixed(1)}</span>
            <span className="text-xs text-white/70 ml-1">g/kg</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-white/50 mt-1 px-1">
          <span>1.0</span>
          <span>1.5</span>
          <span>2.0</span>
          <span>2.5</span>
          <span>3.0</span>
        </div>
        {goals.protein_per_kg && (
          <p className="text-xs text-white/60 mt-2">
            Empfehlung: 1.6-2.2 g/kg je nach Ziel. Wert wird beim nächsten Speichern mit den Makros synchronisiert.
          </p>
        )}
      </div>

      {/* Macro Distribution */}
      <div className="p-4 bg-white/5 rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-white/80" />
            <div>
              <h3 className="font-semibold">Makronährstoffe</h3>
              <p className="text-xs text-white/70">Verteilung in Prozent</p>
            </div>
          </div>
          <span className={`text-sm font-medium inline-flex items-center gap-1 ${macroSum === 100 ? 'text-green-600' : 'text-red-600'}`}>
            {macroSum === 100 ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {macroSum}%
          </span>
        </div>

        {/* Macro Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(MACRO_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyMacroPreset(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                goals.preset === key
                  ? 'bg-green-500 text-white'
                  : 'bg-white/5 text-white/80 hover:bg-white/10'
              }`}
            >
              <span className="inline-flex items-center gap-1"><preset.icon className="w-3.5 h-3.5" />{preset.name}</span>
            </button>
          ))}
        </div>

        {/* Macro Inputs */}
        <div className="grid grid-cols-3 gap-4">
          {/* Protein */}
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-lg font-bold text-blue-600">P</span>
            </div>
            <Label className="text-xs text-white/70 mb-1 block">Protein</Label>
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="5"
                  max="60"
                  value={goals.protein_percent}
                  onChange={(e) => setGoals(prev => ({ ...prev, protein_percent: parseInt(e.target.value) || 0 }))}
                  className="w-14 text-center font-bold text-sm"
                />
                <span className="text-white/70 text-xs">%</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="10"
                  max="500"
                  value={proteinGrams}
                  onChange={(e) => {
                    const newGrams = parseInt(e.target.value) || 0;
                    const newPercent = calculatePercent(newGrams, goals.daily_calories, PROTEIN_KCAL_PER_GRAM);
                    setGoals(prev => ({ ...prev, protein_percent: Math.min(newPercent, 100) }));
                  }}
                  className="w-14 text-center text-sm"
                />
                <span className="text-white/60 text-xs">g</span>
              </div>
            </div>
          </div>

          {/* Carbs */}
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-yellow-100 flex items-center justify-center">
              <span className="text-lg font-bold text-yellow-600">K</span>
            </div>
            <Label className="text-xs text-white/70 mb-1 block">Kohlenhydrate</Label>
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="5"
                  max="70"
                  value={goals.carbs_percent}
                  onChange={(e) => setGoals(prev => ({ ...prev, carbs_percent: parseInt(e.target.value) || 0 }))}
                  className="w-14 text-center font-bold text-sm"
                />
                <span className="text-white/70 text-xs">%</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="10"
                  max="800"
                  value={carbsGrams}
                  onChange={(e) => {
                    const newGrams = parseInt(e.target.value) || 0;
                    const newPercent = calculatePercent(newGrams, goals.daily_calories, CARBS_KCAL_PER_GRAM);
                    setGoals(prev => ({ ...prev, carbs_percent: Math.min(newPercent, 100) }));
                  }}
                  className="w-14 text-center text-sm"
                />
                <span className="text-white/60 text-xs">g</span>
              </div>
            </div>
          </div>

          {/* Fat */}
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-lg font-bold text-red-600">F</span>
            </div>
            <Label className="text-xs text-white/70 mb-1 block">Fett</Label>
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="10"
                  max="80"
                  value={goals.fat_percent}
                  onChange={(e) => setGoals(prev => ({ ...prev, fat_percent: parseInt(e.target.value) || 0 }))}
                  className="w-14 text-center font-bold text-sm"
                />
                <span className="text-white/70 text-xs">%</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <Input
                  type="number"
                  min="10"
                  max="300"
                  value={fatGrams}
                  onChange={(e) => {
                    const newGrams = parseInt(e.target.value) || 0;
                    const newPercent = calculatePercent(newGrams, goals.daily_calories, FAT_KCAL_PER_GRAM);
                    setGoals(prev => ({ ...prev, fat_percent: Math.min(newPercent, 100) }));
                  }}
                  className="w-14 text-center text-sm"
                />
                <span className="text-white/60 text-xs">g</span>
              </div>
            </div>
          </div>
        </div>

        {macroSum !== 100 && (
          <p className="text-xs text-red-600 mt-3 text-center">
            Die Summe muss 100% ergeben ({100 - macroSum > 0 ? `+${100 - macroSum}%` : `${100 - macroSum}%`} benötigt)
          </p>
        )}
      </div>

      {/* Meal Distribution */}
      <div className="p-4 bg-white/5 rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-white/80" />
            <div>
              <h3 className="font-semibold">Mahlzeiten-Verteilung</h3>
              <p className="text-xs text-white/70">Kalorien pro Mahlzeit</p>
            </div>
          </div>
          <span className={`text-sm font-medium inline-flex items-center gap-1 ${mealSum === 100 ? 'text-green-600' : 'text-red-600'}`}>
            {mealSum === 100 ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {mealSum}%
          </span>
        </div>

        {/* Meal Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(MEAL_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyMealPreset(key)}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-white/80 hover:bg-white/10 transition-all"
            >
              {preset.name}
            </button>
          ))}
        </div>

        {/* Meal Inputs */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { key: 'breakfast', label: 'Frühstück', icon: Sunrise },
            { key: 'lunch', label: 'Mittag', icon: Sun },
            { key: 'dinner', label: 'Abend', icon: Moon },
            { key: 'snacks', label: 'Snacks', icon: Cookie }
          ].map(meal => (
            <div key={meal.key} className="text-center">
              <div className="flex justify-center"><meal.icon className="w-5 h-5 text-white/80" /></div>
              <Label className="block text-xs text-white/70 mt-1">{meal.label}</Label>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Input
                  type="number"
                  min="0"
                  max="60"
                  value={goals.meal_distribution[meal.key]}
                  onChange={(e) => setGoals(prev => ({
                    ...prev,
                    meal_distribution: {
                      ...prev.meal_distribution,
                      [meal.key]: parseInt(e.target.value) || 0
                    }
                  }))}
                  className="w-14 text-center text-sm font-bold"
                />
                <span className="text-xs text-white/70">%</span>
              </div>
              <p className="text-xs text-white/70 mt-1">
                {calculateMealCalories(goals.meal_distribution[meal.key], goals.daily_calories)} kcal
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Water Goal */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <Droplet className="w-5 h-5 text-blue-500" />
          <div>
            <h3 className="font-semibold">Wasser-Ziel</h3>
            <p className="text-xs text-white/70">Tägliche Wassermenge</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="1000"
            max="5000"
            step="250"
            value={goals.water_goal_ml}
            onChange={(e) => setGoals(prev => ({ ...prev, water_goal_ml: parseInt(e.target.value) || 2500 }))}
            className="flex-1 text-center font-bold"
          />
          <span className="text-white/80">ml</span>
        </div>
      </div>

      {/* Save Button */}
      <Button 
        onClick={handleSave}
        disabled={saving || macroSum !== 100 || mealSum !== 100}
        className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold"
      >
        {saving ? 'Speichern...' : 'Ziele speichern'}
      </Button>
    </div>
  );
};

export default NutritionGoalsEditor;
